/**
 * The position, in plain words — ROADMAP 5.26.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * "MAX LOSS 3.26 L · BREAKEVENS 23,162 · 23,513" IS NOT AN EXPLANATION
 *
 * It is four correct numbers that leave the reader to assemble the trade in
 * their head: which side of the breakeven is the good side, whether the
 * loss happens above or below, whether the profit is capped. Everyone who
 * can assemble it had already read the curve; everyone who cannot is who
 * the line was for.
 *
 * So this says it, in the order a person asks and in the words they would
 * use:
 *
 *   You can make at most ₹3,315 if NIFTY expires between 23,000 and
 *   23,300. You start losing money below 22,949 or above 23,351. A sharp
 *   move costs you at most ₹9,685, whichever way it goes.
 *
 * THE PROFIT ZONE IS THE STRIKES; THE WARNING IS THE BREAKEVENS.
 *
 * Different numbers, used for different things — "where do I want it to
 * land" against "where does this turn against me" — and an explanation
 * that quotes one for the other is worse than none. The first sentence
 * takes the PLATEAU, found by evaluating the payoff at each strike; the
 * second takes the breakevens.
 *
 * DERIVED FROM THE SHAPE, NEVER WRITTEN PER STRATEGY. `payoffShape` gives
 * the extremes, the breakevens and both tail slopes, and the sentences are
 * a reading of those — so a Bear Call Spread is explained by the payoff it
 * HAS rather than by its name, and an edited leg, or a structure the
 * catalogue has never heard of, is still described correctly.
 *
 * TOKENS, NOT A STRING. The caller colours the money, the levels and the
 * instrument differently, and a sentence returned as text would have to be
 * parsed back apart to do it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { kinksOf, payoffAt, payoffShape } from './basketPayoff.js';
const t = (text) => ({ kind: 'text', text });
const profit = (text) => ({ kind: 'profit', text });
const loss = (text) => ({ kind: 'loss', text });
/** Near enough to the best case to count as the same plateau. */
const SAME = 0.005;
/**
 * Where the best case actually happens.
 *
 * A piecewise-linear payoff takes its maximum on a kink or in a tail, so
 * evaluating the strikes finds the plateau exactly — and the plateau is
 * what a trader means by "where do I want it to expire".
 */
function plateauOf(legs, best) {
    const kinks = kinksOf(legs);
    if (kinks.length === 0 || !(Math.abs(best) > 0))
        return null;
    const tolerance = Math.abs(best) * SAME;
    const near = kinks.filter((k) => Math.abs(payoffAt(legs, k) - best) <= tolerance);
    if (near.length === 0)
        return null;
    const lowest = Math.min(...near);
    const highest = Math.max(...near);
    const step = Math.max(1, highest * 0.02);
    // Does it still earn the maximum PAST the outermost strike? Then that
    // side is open, and saying "between" would invent a ceiling the trade
    // does not have.
    const openAbove = payoffAt(legs, highest + step) >= best - tolerance;
    const openBelow = payoffAt(legs, Math.max(0, lowest - step)) >= best - tolerance;
    return { from: openBelow ? null : lowest, to: openAbove ? null : highest };
}
/** What the position is worth far above, and far below, the strikes. */
function tails(legs) {
    const kinks = kinksOf(legs);
    const highest = kinks.length > 0 ? Math.max(...kinks) : 0;
    return { up: payoffAt(legs, highest * 2 + 1000), down: payoffAt(legs, 0) };
}
/**
 * Three short sentences: where the money is, where it turns, and what a
 * sharp move does.
 *
 * Returns an empty array when there is nothing true to say. A sentence
 * about a position that does not exist is worse than silence on a screen
 * used to place orders.
 */
export function explainPayoff(input, fmt) {
    const { legs, symbol } = input;
    if (legs.length === 0)
        return [];
    const shape = payoffShape(legs);
    const sym = { kind: 'symbol', text: symbol };
    const lvl = (v) => ({ kind: 'level', text: fmt.level(v) });
    const bes = [...shape.breakevens].sort((a, b) => a - b);
    const best = shape.maxProfit.kind === 'FINITE' ? shape.maxProfit.value : null;
    const worst = shape.maxLoss.kind === 'FINITE' ? Math.abs(shape.maxLoss.value) : null;
    const tail = tails(legs);
    const lines = [];
    // ── 1. where the money is ────────────────────────────────────────────
    /*
     * A FALLING TAIL IS AN OPEN UPSIDE TOO, even though it has a number.
     *
     * Spot cannot go below zero, so a long put's best case is finite — and
     * reporting it as "at most ₹14,95,325" is true, useless, and not how
     * anybody holds a put. When the maximum is only reached at zero, the
     * sentence describes the direction instead, exactly as it does for a
     * call's unbounded upside.
     */
    const openUp = shape.maxProfit.kind === 'UNBOUNDED';
    const openDown = best !== null &&
        shape.slopeBelow < 0 &&
        Math.abs(payoffAt(legs, 0) - best) <= Math.abs(best) * SAME;
    if (best === null || best <= 0 || openUp || openDown) {
        const up = !openDown && shape.slopeAbove > 0;
        lines.push([
            t('You make more the further '),
            sym,
            t(up ? ' rises' : ' falls'),
            ...(bes.length > 0
                ? [t(up ? ' above ' : ' below '), lvl(up ? bes[bes.length - 1] : bes[0])]
                : []),
            t(', with no limit on the profit.'),
        ]);
    }
    else {
        const zone = plateauOf(legs, best);
        const where = zone === null
            ? []
            : zone.from !== null && zone.to !== null && zone.from !== zone.to
                ? [t(' if '), sym, t(' expires between '), lvl(zone.from), t(' and '), lvl(zone.to)]
                : zone.from !== null && zone.to === null
                    ? [t(' if '), sym, t(' expires at or above '), lvl(zone.from)]
                    : zone.to !== null && zone.from === null
                        ? [t(' if '), sym, t(' expires at or below '), lvl(zone.to)]
                        : [t(' if '), sym, t(' expires at '), lvl(zone.from ?? zone.to ?? 0)];
        lines.push([t('You can make at most '), profit(fmt.money(best)), ...where, t('.')]);
    }
    // ── 2. where it turns against you ────────────────────────────────────
    /*
     * WHICH SIDE OF A RANGE PAYS IS SAMPLED, not assumed. A sold straddle
     * pays in the middle and a bought one loses there, and the first cut of
     * this told a long straddle it lost OUTSIDE its breakevens — exactly
     * backwards, on the sentence a reader trusts most.
     */
    const insidePays = bes.length >= 2 ? payoffAt(legs, (bes[0] + bes[bes.length - 1]) / 2) > 0 : false;
    if (bes.length >= 2 && insidePays) {
        lines.push([
            t('You start losing money below '),
            lvl(bes[0]),
            t(' or above '),
            lvl(bes[bes.length - 1]),
            t('.'),
        ]);
    }
    else if (bes.length >= 2) {
        lines.push([
            t('Between '),
            lvl(bes[0]),
            t(' and '),
            lvl(bes[bes.length - 1]),
            t(' you lose money, so it has to move far enough to clear one of them.'),
        ]);
    }
    else if (bes.length === 1) {
        const be = bes[0];
        const step = Math.max(1, be * 0.01);
        const paysAbove = payoffAt(legs, be + step) > payoffAt(legs, be - step);
        lines.push([t(paysAbove ? 'Below ' : 'Above '), lvl(be), t(' you start losing money.')]);
    }
    else {
        // No crossing at all: the trade is already decided, either way.
        const flat = payoffAt(legs, kinksOf(legs)[0] ?? 0);
        lines.push(flat >= 0
            ? [t('There is no price at which this one turns into a loss.')]
            : [t('There is no price at which this one turns into a profit.')]);
    }
    // ── 3. what a sharp move does ────────────────────────────────────────
    if (shape.maxLoss.kind === 'UNBOUNDED') {
        lines.push([
            t('If '),
            sym,
            t(' rises sharply there is no limit to the loss — it keeps growing with every point, so this one needs a stop.'),
        ]);
    }
    else if (worst !== null && worst > 0) {
        /*
         * The far tails decide which way is worth warning about, and whether
         * the other side still pays something. A structure whose profit
         * merely SHRINKS on a sharp move is a very different trade from one
         * that turns into a loss, and the difference is the whole reason a
         * jade lizard exists.
         */
        const stillPays = Math.min(tail.up, tail.down) > 0;
        const shrinks = tail.up > 0 && tail.up < best ? 'up' : tail.down > 0 && tail.down < best ? 'down' : null;
        /*
         * WHERE the worst case happens, when that is a real price. On a
         * bought straddle it is the strike — the single most useful number in
         * the sentence. On a long call it is zero, which is not a price
         * anybody needs told, so the clause is dropped.
         */
        const at = shape.maxLoss.kind === 'FINITE' ? shape.maxLoss.atSpot : 0;
        const atClause = at > 0 ? [t(', if '), sym, t(' expires at '), lvl(at)] : [];
        if (!insidePays && bes.length >= 2) {
            lines.push([t('The most you can lose is '), loss(fmt.money(worst)), ...atClause, t('.')]);
        }
        else if (stillPays) {
            lines.push([
                t('Even a sharp move either way leaves you in profit — at least '),
                profit(fmt.money(Math.min(tail.up, tail.down))),
                t('.'),
            ]);
        }
        else if (shrinks !== null) {
            lines.push([
                t('If '),
                sym,
                t(shrinks === 'up' ? ' rises sharply' : ' falls sharply'),
                t(' your profit shrinks, but you still keep '),
                profit(fmt.money(shrinks === 'up' ? tail.up : tail.down)),
                t('. The other way costs you at most '),
                loss(fmt.money(worst)),
                t('.'),
            ]);
        }
        else {
            lines.push([t('The most you can lose is '), loss(fmt.money(worst)), ...atClause, t('.')]);
        }
    }
    return lines;
}
/** Flatten to plain text — for a title attribute, a log, or a test. */
export function explainText(lines) {
    return lines.map((line) => line.map((token) => token.text).join('')).join(' ');
}
//# sourceMappingURL=payoffExplain.js.map